import { type SelectProps } from "antd/lib/select";
import { PureComponent } from "react";

import { cn } from "@btnet/utilities-core";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { track } from "utilities/analytics/analytics";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import "./ColorPicker.less";

import { colors } from "./ColorPicker.types";

interface IColorPickerProps<FormValues> {
    /**
     * fieldname of the input
     * pass formik setFieldValue
     */
    id: KeyOfOrString<FormValues> & string;

    className?: string;

    "data-testid": string;

    readOnly?: boolean;

    /** Pass in formik setFieldValue, you should store the color hex value in formik. Convert to id using getColorIdFromHex(color) if needed */
    onChange: (
        id: string,
        color: string | undefined,
        colorOption: BTSelectItem | undefined,
    ) => void;

    /** Pass in formik setFieldTouched */
    onBlur: (id: string, touched: boolean) => void;

    /** hex value of the color to select */
    value?: string;

    disabled?: boolean;
}

type ColorPickerMergedProps<FormValues> = Omit<
    SelectProps<FormValues> | "color",
    "onChange" | "onBlur" | "treeData"
> &
    IColorPickerProps<FormValues>;

class ColorPickerBase<FormValues> extends PureComponent<ColorPickerMergedProps<FormValues>> {
    private handleChange = (
        field: string,
        value: string | undefined,
        selectedItem: BTSelectItem<undefined> | undefined,
    ) => {
        this.props.onChange(this.props.id, value, selectedItem);
    };

    render() {
        const { className, value, id, onChange, readOnly, onBlur, ...passThrough } = this.props;

        if (readOnly) {
            const selectedItem = colors.find((item) => item.value === value);
            return (
                <ValueDisplay
                    data-testid={this.props["data-testid"]}
                    value={
                        value !== undefined ? (
                            <>
                                <div
                                    className="color-picker-swatch"
                                    style={{
                                        background: value,
                                    }}
                                />
                                {selectedItem?.title}
                            </>
                        ) : undefined
                    }
                />
            );
        }

        const classNameWithDefault = cn(
            "ColorPicker",
            {
                ColorPickerCursorPadding: value !== undefined,
            },
            className,
        );

        return (
            <BTSelect
                id={id}
                value={value}
                onChange={this.handleChange}
                onBlur={onBlur}
                treeData={colors}
                className={classNameWithDefault}
                readOnly={readOnly}
                customItemRender={(selectItem) => (
                    <>
                        <div
                            className="color-picker-swatch"
                            style={{ background: selectItem.value }}
                        />
                        {selectItem.title}
                    </>
                )}
                {...passThrough}
            />
        );
    }
}

/**
 * Renders a dropdown with color swatches
 * @example
 * <ColorPicker<IFormValues> id="myColor" value={value.myColor} onChange={setFieldValue} />
 */
export const ColorPicker = track((props) => ({
    component: "Color Picker",
    uniqueId: props["data-testid"],
}))(ColorPickerBase);
